import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import logo from "../../Assets/Logos/Logo.svg";
import { ChevronDown } from "lucide-react";
import InquiryModal from "../User/Common/InquiryModal";
import axios from "axios";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [activeItem, setActiveItem] = useState("");
  const [services, setServices] = useState([]);
  const [serviceOpen, setServiceOpen] = useState(false);
  const serviceRef = useRef(null);

  const[isModalOpen, setIsModalOpen]  = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (serviceRef.current && !serviceRef.current.contains(e.target)) {
        setServiceOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getAllService = async ()=>{
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/product/navbar-services`);
      // console.log("service", res.data.data);
      setServices(res.data.data);
    } catch (error) {
      console.log("Error is occured", error);
    }
  }

  useEffect(()=>{
    getAllService();
  },[]);

  // Update active item based on current path
  useEffect(() => {
    const path = location.pathname;

    // Set active item based on path
    if (path === "/") setActiveItem("Home");
    else if (path === "/about") setActiveItem("About");
    else if (path === "/services" || path.includes("/services/"))
      setActiveItem("Service");
    else if (path === "/portfolio") setActiveItem("Portfolio");
    else if (path === "/career") setActiveItem("Career");
    else if (path === "/contact") setActiveItem("Contact Us");
    else setActiveItem("");
  }, [location]);

  // Navigation handler
  const handleNavClick = (item) => {
    setActiveItem(item);
    setMobileOpen(false); 
    window.scrollTo(0,0);
  };

  return (
    <nav className="w-full bg-[#F1F3F8] shadow-md sticky top-0 z-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-20">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" onClick={() => handleNavClick("Home")}>
            <div className="flex items-center gap-2 max-w-52">
              <img src={logo} alt="Solarex" className="w-full h-auto object-cover" />
            </div>
          </Link>

          {/* Desktop Menu */}
          <ul className="hidden lg:flex items-center gap-8 font-medium text-gray-800">
            <li>
              <Link
                to="/"
                onClick={() => handleNavClick("Home")}
                className={`hover:text-green-600 font-josefin font-bold cursor-pointer ${
                  activeItem === "Home"
                    ? "text-green-600 font-bold text-lg"
                    : ""
                }`}
              >
                Home
              </Link>
            </li>

            <li>
              <Link
                to="/about"
                onClick={() => handleNavClick("About")}
                className={`hover:text-green-600 font-josefin font-bold cursor-pointer ${
                  activeItem === "About"
                    ? "text-green-600 font-bold text-lg"
                    : ""
                }`}
              >
                About
              </Link>
            </li>

            {/* Service Dropdown */}
            <li ref={serviceRef} className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setServiceOpen(!serviceOpen);
                }}
                className={`hover:text-green-600 font-josefin font-bold flex items-center gap-1 ${
                  activeItem === "Service"
                    ? "text-green-600 font-bold text-lg"
                    : ""
                }`}
              >
                Service
                <ChevronDown
                  className={`inline-block ml-1 transition-transform duration-200 ${
                    serviceOpen ? "rotate-180" : ""
                  }`}
                  size={16}
                />
              </button>
              {serviceOpen && (
                <div className="absolute left-0 top-full mt-3 w-56 bg-white shadow-lg rounded-lg z-50 text-gray-800">
                  <ul className="py-2">
                  {services.map((service, index) => (
                    <li
                      key={service._id}
                      className="relative group border-b border-gray-100 last:border-0 transition-all duration-200 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="px-5 py-3.5">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium group-hover:text-green-600 transition-colors duration-200">
                            {service.name}
                          </span>
                          {service.hasMultipleProducts && (
                            <span className="text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all duration-200 text-lg">›</span>
                          )}
                        </div>

                        {/* SUB MENU - Enhanced design */}
                        {service.hasMultipleProducts && (
                          <div className="absolute left-full top-0 ml-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden">
                            {/* Submenu header with gradient */}
                            <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2.5">
                              <h4 className="text-white font-medium text-sm flex items-center gap-2">
                                <span className="w-1 h-4 bg-white/30 rounded-full"></span>
                                {service.name}
                              </h4>
                            </div>
                            
                            {/* Submenu items */}
                            <ul className="py-1 max-h-80 overflow-y-auto">
                              {service.products.map((product, idx) => (
                                <li
                                  key={product._id}
                                  className="border-b border-gray-50 last:border-0 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-150"
                                >
                                  <Link
                                    to={`/serviceDetails/${product._id}`}
                                    onClick={() => {
                                      handleNavClick("Service");
                                      setServiceOpen(false);
                                      window.scrollTo(0, 0);
                                    }}
                                    className="block px-4 py-3 text-sm text-gray-600 hover:text-green-600 hover:pl-5 transition-all duration-200"
                                  >
                                    <span className="flex items-center gap-2">
                                      <span className="w-1 h-1 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                                      {product.heading}
                                    </span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* SINGLE PRODUCT - Enhanced with hover effect */}
                        {!service.hasMultipleProducts && service.defaultProduct && (
                          <Link
                            to={`/serviceDetails/${service.defaultProduct._id}`}
                            onClick={() => {
                              handleNavClick("Service");
                              setServiceOpen(false);
                            }}
                            className="absolute inset-0 group-hover:bg-gradient-to-r group-hover:from-green-50/50 group-hover:to-emerald-50/50 transition-all duration-200"
                          >
                            <span className="sr-only">View {service.name}</span>
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                  </ul>
                </div>
              )}
            </li>


            <li>
              <Link
                to="/career"
                onClick={() => handleNavClick("Career")}
                className={`hover:text-green-600 font-josefin font-bold cursor-pointer ${
                  activeItem === "Career"
                    ? "text-green-600 font-bold text-lg"
                    : ""
                }`}
              >
                Notice
              </Link>
            </li>

             <li>
              <Link
                to="/user-blog"
                onClick={() => handleNavClick("Blog")}
                className={`hover:text-green-600 font-josefin font-bold cursor-pointer ${
                  activeItem === "Blog"
                    ? "text-green-600 font-bold text-lg"
                    : ""
                }`}
              >
                Blog
              </Link>
            </li>

            <li>
              <Link
                to="/contact"
                onClick={() => handleNavClick("Contact Us")}
                className={`hover:text-green-700 font-josefin font-bold cursor-pointer ${
                  activeItem === "Contact Us"
                    ? "text-green-700 font-bold text-lg"
                    : ""
                }`}
              >
                Contact Us
              </Link>
            </li>
          </ul>

          {/* Inquiry Button */}
          <div className="hidden lg:block">
              <button 
              onClick={()=>{
                setIsModalOpen(true);
              }}
              className="bg-gray-200 px-5 py-2 font-josefin font-bold rounded-lg text-lg hover:bg-green-600 hover:text-white transition">
                Inquiry Now
              </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-2xl text-black"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
    {mobileOpen && (
      <div className="lg:hidden fixed inset-x-0 top-16 bg-white/95 backdrop-blur-sm shadow-xl rounded-b-2xl border-t border-gray-100 px-6 py-6 space-y-4 animate-slideDown max-h-[calc(100vh-4rem)] overflow-y-auto">
        {/* Home Link */}
        <Link
          to="/"
          onClick={() => handleNavClick("Home")}
          className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-300 ${
            activeItem === "Home" 
              ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 font-semibold shadow-sm" 
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span>Home</span>
        </Link>

        {/* About Link */}
        <Link
          to="/about"
          onClick={() => handleNavClick("About")}
          className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-300 ${
            activeItem === "About" 
              ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 font-semibold shadow-sm" 
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>About</span>
        </Link>

        {/* Service Dropdown */}
        <details className="group">
          <summary
            className={`flex items-center space-x-3 p-3 rounded-xl list-none cursor-pointer transition-all duration-300 ${
              activeItem === "Service" 
                ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 font-semibold shadow-sm" 
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="flex-1">Service</span>
            <svg className="w-4 h-4 transition-transform duration-300 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>

          <div className="ml-6 mt-3 space-y-2 border-l-2 border-green-100 pl-4">
            {services.map((service) => (
              <div key={service._id} className="animate-fadeIn">
                {service.hasMultipleProducts ? (
                  <details className="group/child">
                    <summary className="flex items-center justify-between p-2 rounded-lg list-none cursor-pointer hover:bg-gray-50 transition-all duration-200 font-medium text-gray-700">
                      <span>{service.name}</span>
                      <svg className="w-3 h-3 transition-transform duration-200 group-open/child:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="ml-4 mt-2 space-y-1 border-l-2 border-gray-100 pl-3">
                      {service.products.map((product) => (
                        <Link
                          key={product._id}
                          to={`/product/${product._id}`}
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 px-3 text-sm text-gray-600 rounded-lg hover:bg-green-50 hover:text-green-700 transition-all duration-200"
                        >
                          {product.heading}
                        </Link>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link
                    to={`/product/${service.defaultProduct?._id}`}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 px-3 text-gray-600 rounded-lg hover:bg-green-50 hover:text-green-700 hover:pl-4 transition-all duration-200"
                  >
                    {service.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </details>

        {/* Career Link */}
        <Link
          to="/career"
          onClick={() => handleNavClick("Career")}
          className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-300 ${
            activeItem === "Career" 
              ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 font-semibold shadow-sm" 
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>Notice</span>
        </Link>

        {/* Contact Us Link */}
        <Link
          to="/contact"
          onClick={() => handleNavClick("Contact Us")}
          className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-300 ${
            activeItem === "Contact Us" 
              ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 font-semibold shadow-sm" 
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>Contact Us</span>
        </Link>

        {/* Inquiry Button */}
        <div>
          <button 
           onClick={()=>{
                setIsModalOpen(true);
              }}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transform transition-all duration-300 flex items-center justify-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>Inquiry Now</span>
          </button>
        </div>

        {/* Decorative gradient line */}
        <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-green-200 to-transparent"></div>
      </div>
    )}
     
      <InquiryModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
      />
    </nav>
  );
}